import { useState, useMemo, useEffect, useRef } from 'react'
import { useQueries } from '@tanstack/react-query'
import { useSelector } from 'react-redux'

import { isEmpty, isEqual, chunk } from 'lodash'

import {
	getSubmissionRecordData,
	getSubmissionRecordImageBlobs,
	getSubmissionRecordImageData,
	getSubmissionRecordImageViewerData,
} from 'components/data/datagrid/recordsService'

import { generateRandomID } from '../../../helpers'

const loadImageViewerData = async (record, token) => {
	let promise = await getSubmissionRecordImageViewerData(record, token)
	return promise
}

const loadImageData = async (record, blob, token) => {
	let promise = await getSubmissionRecordImageData(record, blob, token)
	return promise
}

export function useImageBlobsRecordQueries(props) {
	const { records } = props
	const { environment } = useSelector((state) => state)

	const [stableRecords, setStableRecords] = useState(records)

	useEffect(() => {
		if (!isEqual(records, stableRecords)) {
			setStableRecords(records)
		}
	}, [records, stableRecords])

	const queries = useMemo(() => {
		return stableRecords.map((record) => {
			const enabled = !isEmpty(record)
			return {
				queryKey: ['imageBlobsRecord', record?.recordKey],
				queryFn: async () => {
					const rec = {
						type: record?.recordType,
						submissionKey: record?.recordKey,
					}

					const currentRecordRes = await getSubmissionRecordData(rec, environment.apiToken)
					const currentRecord = await currentRecordRes?.data
					if (!currentRecord || !currentRecord?.submissionKey) {
						return {
							...currentRecordRes?.data,
							recordKey: record?.recordKey,
						}
					}

					const recordBlobsRes = await getSubmissionRecordImageBlobs(
						currentRecord,
						environment.apiToken
					)

					return {
						...currentRecordRes?.data,
						recordKey: record?.recordKey,
						blobs: recordBlobsRes.data,
					}
				},
				enabled,
				staleTime: Infinity,
				chunk,
			}
		})
	}, [stableRecords, environment.apiToken])

	const queriesResult = useQueries({ queries })

	const data = useMemo(() => {
		return queriesResult
			.map((result) => result.data)
			.filter(Boolean)
			.flat()
	}, [queriesResult])

	const isLoading = queriesResult.some((result) => result.isLoading)
	const isError = queriesResult.some((result) => result.isError)
	const error = queriesResult.find((result) => result.isError)?.error || null

	return {
		data,
		isLoading,
		isError,
		error,
	}
}

export function useImageViewerRecordQueries({ records, selectConnectedGridKey }) {
	const { environment } = useSelector((state) => state)

	const selectedConnectedDatagridRef = useRef(selectConnectedGridKey)
	const prevRecordsRef = useRef()
	const [stableRecords, setStableRecords] = useState(records)
	const [queries, setQueries] = useState([])

	useEffect(() => {
		if (!isEqual(records, prevRecordsRef.current)) {
			prevRecordsRef.current = records
			setStableRecords(records)
		}
	}, [records])

	function resetData() {
		setQueries([])
		setAllData([])
	}

	useEffect(() => {
		if (selectedConnectedDatagridRef.current === selectConnectedGridKey) return
		selectedConnectedDatagridRef.current = selectConnectedGridKey
		resetData()
	}, [selectConnectedGridKey])

	useEffect(() => {
		if (isEmpty(stableRecords)) {
			setQueries([])
			return
		}

		const newQueries = stableRecords.map((record) => {
			const enabled = !isEmpty(record)
			return {
				queryKey: ['imageViewerRecord', record?.recordKey],
				queryFn: async () => {
					const res = await loadImageViewerData(record, environment.apiToken)
					const imageViewerList = res?.data || []

					if (isEmpty(imageViewerList)) return []

					return imageViewerList.map((item) => ({
						key: generateRandomID(),
						src: item.text,
						alt: item.name,
						width: 1000,
						height: 1000,
						description: item.name,
					}))
				},
				enabled,
				staleTime: Infinity,
			}
		})

		setQueries(newQueries)
	}, [stableRecords, environment.apiToken])

	const queriesResult = useQueries({ queries })

	const [allData, setAllData] = useState([])

	useEffect(() => {
		const merged = queriesResult
			.map((r) => r.data)
			.filter(Boolean)
			.flat()
		setAllData((prev) => (isEqual(prev, merged) ? prev : merged))
	}, [queriesResult])

	const isLoading = queriesResult.some((r) => r.isLoading)
	const isError = queriesResult.some((r) => r.isError)
	const error = queriesResult.find((r) => r.isError)?.error || null

	return {
		data: allData,
		isLoading,
		isError,
		error,
	}
}

export function useLoadBlobImageQueries({
	records,
	isConnectedToHiddenDatagrid = false,
	selectConnectedGridKey = null,
}) {
	const { environment } = useSelector((state) => state)

	const seenBlobKeysRef = useRef(new Set()) // prevent duplicate queries
	const selectedConnectedDatagridRef = useRef(selectConnectedGridKey)
	const prevRecordsRef = useRef()
	useEffect(() => {
		if (!isEqual(records, prevRecordsRef.current)) {
			prevRecordsRef.current = records
			if (!isConnectedToHiddenDatagrid) {
				setQueries([])
				seenBlobKeysRef.current.clear()
			}
		}
	}, [records, isConnectedToHiddenDatagrid])
	const [queries, setQueries] = useState([])

	useEffect(() => {
		if (isEmpty(records)) {
			if (isEqual(queries, [])) return
			resetData()
			seenBlobKeysRef.current.clear()
			return
		}

		const newQueries = []

		records.forEach((record) => {
			const enabled = !isEmpty(record)
			record?.blobs?.forEach((blob) => {
				const uniqueKey = `${record?.recordKey}-${blob?.key}`
				if (seenBlobKeysRef.current.has(uniqueKey)) return
				seenBlobKeysRef.current.add(uniqueKey)

				newQueries.push({
					queryKey: ['loadBlobImage', record?.recordKey, blob?.key],
					queryFn: async () => {
						const res = await loadImageData(record, blob, environment.apiToken)

						if (['image', 'sketch', 'signature'].includes(blob?.field?.type)) {
							return new Promise((resolve) => {
								const img = new Image()
								img.src = URL.createObjectURL(res.data)
								img.onload = function () {
									resolve({
										key: blob.key,
										src: this.src,
										width: this.width,
										height: this.height,
										alt: blob.fileName,
										description: blob.field?.options?.label?.en || '',
										type: 'image',
									})
								}
								img.onerror = () => resolve(null)
							})
						}

						if (blob?.field?.type === 'location') {
							return new Promise((resolve) => {
								const img = new Image()
								img.src = URL.createObjectURL(res.data)
								img.onload = function () {
									resolve({
										type: 'location',
										key: blob.key,
										src: this.src,
										width: this.width,
										height: this.height,
										alt: blob.fileName,
										description: blob.field?.options?.label?.en || '',
									})
								}
								img.onerror = () => resolve(null)
							})
						}

						if (blob?.field?.type === 'video') {
							return {
								type: 'video',
								blob: res.data,
								fileName: blob.fileName,
								fileType: blob.type,
							}
						}

						if (blob?.field?.type === 'audio') {
							return {
								type: 'audio',
								blob: res.data,
								fileName: blob.fileName,
								fileType: blob.type,
							}
						}

						return null
					},
					enabled,
					staleTime: Infinity,
				})
			})
		})

		if (newQueries.length > 0) {
			setQueries((prev) => {
				const unique = newQueries.filter((q) => !prev.some((p) => isEqual(p.queryKey, q.queryKey)))
				return unique.length > 0 ? [...prev, ...unique] : prev
			})
		}
	}, [records])

	const queriesResult = useQueries({ queries })

	const [allData, setAllData] = useState({
		imageList: [],
		videoList: [],
		audioList: [],
		locationList: [],
	})

	function resetData() {
		setQueries([])
		setAllData({
			imageList: [],
			videoList: [],
			audioList: [],
			locationList: [],
		})
	}

	useEffect(() => {
		if (selectedConnectedDatagridRef.current === selectConnectedGridKey) return

		selectedConnectedDatagridRef.current = selectConnectedGridKey
		seenBlobKeysRef.current.clear()
		resetData()
	}, [selectConnectedGridKey])

	useEffect(() => {
		const merged = queriesResult?.map((r) => r.data).filter(Boolean)
		if (isEmpty(merged)) return

		const newData = {
			imageList: merged.filter((i) => i?.type === 'image'),
			videoList: merged.filter((i) => i?.type === 'video'),
			audioList: merged.filter((i) => i?.type === 'audio'),
			locationList: merged.filter((i) => i?.type === 'location'),
		}

		setAllData((prev) => (isEqual(prev, newData) ? prev : newData))
	}, [JSON.stringify(queriesResult)])

	const isLoading = queriesResult.some((r) => r.isLoading)
	const isError = queriesResult.some((r) => r.isError)
	const error = queriesResult.find((r) => r.isError)?.error || null

	return {
		imageList: allData?.imageList,
		videoList: allData?.videoList,
		audioList: allData?.audioList,
		locationList: allData?.locationList,
		isLoading,
		isError,
		error,
	}
}
